import { authenticatedFetch } from "./http";
import type { ApplicationSetting, Asn, AsnLine, HandlingUnit, InboundAuditEntry, NumberingPolicy, NumberingValidation, ProcessPoint, Receipt, ReceiptLine } from "./models";

export function loadAsns(): Promise<Asn[]> {
  return authenticatedFetch<Asn[]>("/api/v1/inbound/asns");
}

export function loadAsn(asnId: string): Promise<Asn> {
  return authenticatedFetch<Asn>(`/api/v1/inbound/asns/${asnId}`);
}

export function createAsn(request: Record<string, unknown>): Promise<Asn> {
  return authenticatedFetch<Asn>("/api/v1/inbound/asns", {
    method: "POST", body: JSON.stringify(request)
  });
}

export interface PurchaseOrderSuggestion {
  replenishmentExecutionId: string;
  purchaseOrderNumber: string;
  clientId: string | null;
  warehouseId: string;
  supplierId: string | null;
  itemId: string;
  itemCode: string;
  itemDisplayName: string;
  remainingQuantity: number;
  uom: string;
  expectedAt: string | null;
  externalReference: string | null;
}

export function loadPurchaseOrderSuggestion(po: string): Promise<PurchaseOrderSuggestion> {
  return authenticatedFetch<PurchaseOrderSuggestion>(
    `/api/v1/inbound/purchase-orders/${encodeURIComponent(po)}/suggestion`
  );
}

export function loadAsnLines(asnId: string): Promise<AsnLine[]> {
  return authenticatedFetch<AsnLine[]>(`/api/v1/inbound/asns/${asnId}/lines`);
}

export function addAsnLine(
  asnId: string, request: Record<string, unknown>
): Promise<AsnLine> {
  return authenticatedFetch<AsnLine>(`/api/v1/inbound/asns/${asnId}/lines`, {
    method: "POST", body: JSON.stringify(request)
  });
}

export function changeAsnStatus(
  asnId: string, status: Asn["status"], version: number
): Promise<Asn> {
  return authenticatedFetch<Asn>(`/api/v1/inbound/asns/${asnId}/status`, {
    method: "PATCH", body: JSON.stringify({ status, version })
  });
}

export function claimAsnCustomsClearance(asn: Asn): Promise<Asn> {
  return authenticatedFetch<Asn>(
    `/api/v1/inbound/asns/${asn.id}/customs/claim`,
    { method: "POST", body: JSON.stringify({ version: asn.version }) }
  );
}

export interface CustomsClearanceDecision {
  invoicePresent: boolean;
  packingListPresent: boolean;
  originProvided: boolean;
  customsReference: string;
  brokerName: string | null;
  physicalCheckCompleted: boolean;
  decision: "CLEARANCE_GRANTED" | "ADDITIONAL_INFO_REQUIRED" | "REFUSED";
  comment: string | null;
}

export function completeAsnCustomsClearance(
  asn: Asn, decision: CustomsClearanceDecision
): Promise<Asn> {
  return authenticatedFetch<Asn>(
    `/api/v1/inbound/asns/${asn.id}/customs/complete`,
    { method: "POST", body: JSON.stringify({ version: asn.version, ...decision }) }
  );
}

export function loadAsnAudit(asnId: string): Promise<InboundAuditEntry[]> {
  return authenticatedFetch<InboundAuditEntry[]>(
    `/api/v1/inbound/asns/${asnId}/audit`
  );
}

export function loadReceipts(asnId: string): Promise<Receipt[]> {
  return authenticatedFetch<Receipt[]>(`/api/v1/inbound/asns/${asnId}/receipts`);
}

export function createReceipt(
  asnId: string, receiptNumber: string | null
): Promise<Receipt> {
  return authenticatedFetch<Receipt>(`/api/v1/inbound/asns/${asnId}/receipts`, {
    method: "POST", body: JSON.stringify({ receiptNumber })
  });
}

export function loadAdHocReceipts(): Promise<Receipt[]> {
  return authenticatedFetch<Receipt[]>("/api/v1/inbound/receipts/ad-hoc");
}

export function createAdHocReceipt(
  request: Record<string, unknown>
): Promise<Receipt> {
  return authenticatedFetch<Receipt>("/api/v1/inbound/receipts/ad-hoc", {
    method: "POST", body: JSON.stringify(request)
  });
}

export function loadNumberingPolicies(): Promise<NumberingPolicy[]> {
  return authenticatedFetch<NumberingPolicy[]>(
    "/api/v1/configuration/numbering"
  );
}

export function validateNumberingPolicy(
  documentType: NumberingPolicy["documentType"],
  request: Record<string, unknown>
): Promise<NumberingValidation> {
  return authenticatedFetch<NumberingValidation>(
    `/api/v1/configuration/numbering/${documentType}/validate`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function activateNumberingPolicy(
  documentType: NumberingPolicy["documentType"],
  request: Record<string, unknown>
): Promise<NumberingPolicy> {
  return authenticatedFetch<NumberingPolicy>(
    `/api/v1/configuration/numbering/${documentType}`,
    { method: "PUT", body: JSON.stringify(request) }
  );
}

export function loadApplicationSettings(): Promise<ApplicationSetting[]> {
  return authenticatedFetch<ApplicationSetting[]>(
    "/api/v1/configuration/settings"
  );
}

export function updateApplicationSetting(
  key: string, value: string, version: number
): Promise<ApplicationSetting> {
  return authenticatedFetch<ApplicationSetting>(
    `/api/v1/configuration/settings/${key}`,
    { method: "PUT", body: JSON.stringify({ value, version }) }
  );
}

export function loadReceiptLines(receiptId: string): Promise<ReceiptLine[]> {
  return authenticatedFetch<ReceiptLine[]>(
    `/api/v1/inbound/receipts/${receiptId}/lines`
  );
}

export function addReceiptLine(
  receiptId: string, request: Record<string, unknown>
): Promise<ReceiptLine> {
  return authenticatedFetch<ReceiptLine>(
    `/api/v1/inbound/receipts/${receiptId}/lines`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function completeReceipt(
  receiptId: string, version: number
): Promise<Receipt> {
  return authenticatedFetch<Receipt>(
    `/api/v1/inbound/receipts/${receiptId}/complete`,
    { method: "POST", body: JSON.stringify({ version }) }
  );
}

export function reopenReceipt(
  receiptId: string, version: number, reason: string
): Promise<Receipt> {
  return authenticatedFetch<Receipt>(
    `/api/v1/inbound/receipts/${receiptId}/reopen`,
    { method: "POST", body: JSON.stringify({ version, reason }) }
  );
}

export function deleteReceiptLine(
  receiptId: string, receiptLineId: string, version: number, reason: string
): Promise<Receipt> {
  return authenticatedFetch<Receipt>(
    `/api/v1/inbound/receipts/${receiptId}/lines/${receiptLineId}`,
    { method: "DELETE", body: JSON.stringify({ version, reason }) }
  );
}

export function loadProcessPoints(warehouseId: string): Promise<ProcessPoint[]> {
  const params = new URLSearchParams({ warehouseId });
  return authenticatedFetch<ProcessPoint[]>(
    `/api/v1/inventory/process-points?${params}`
  );
}

export function loadReceiptStock(receiptId: string): Promise<HandlingUnit[]> {
  return authenticatedFetch<HandlingUnit[]>(
    `/api/v1/inventory/receipts/${receiptId}/handling-units`
  );
}
